import React, { useEffect, useRef, useState } from 'react'

import {
    Button,
    Modal,
    Paper,
    Slide,
    Theme,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    TextField,
    InputAdornment,
} from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SearchIcon from '@material-ui/icons/Search'

import { Container, MessageForm } from '../../../../components'
import { FormState } from '../../types'
import {
    ApartmentDeadline,
    DeadlineDetail,
    IdBoolMap,
} from '../../../../models'
import { APARTMENT, toMMDDYYYY } from '../../../../helpers'
import { DeadlineRow } from './DeadlineRow'
import { useUser } from '../../../../hooks'
import { toast } from 'react-toastify'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    theme: Theme
    formState: FormState
    deadlineList: ApartmentDeadline[]
    onClose: () => void
    getDeadlineDetail: (deadlineId: number) => Promise<DeadlineDetail>
    transitionDeadline: (
        deadlineId: number,
        complete: boolean,
    ) => Promise<DeadlineDetail>
    incrementMessageCount: (deadline: ApartmentDeadline) => void
    deleteApartmentDeadline: (apartmentDeadlineId: number) => Promise<void>
    addApartmentDeadlines: (request: {
        deadline_id: number
        apartment_ids: number[]
    }) => Promise<DeadlineDetail>
}

const DESIRED_SIZE_PERCENT = 0.8

const screenH = window.innerHeight
const screenW = window.innerWidth
const aspectRatio = screenH / screenW

const h = screenH * DESIRED_SIZE_PERCENT
const w = screenW * DESIRED_SIZE_PERCENT * aspectRatio

export const DeadlineDetailModal = (props: Props) => {
    const {
        theme,
        onClose,
        formState,
        getDeadlineDetail,
        transitionDeadline,
        incrementMessageCount,
        deadlineList,
        deleteApartmentDeadline,
        addApartmentDeadlines,
    } = props

    const [deadlineDetail, setDeadlineDetail] = useState<DeadlineDetail | null>(
        null,
    )

    const [openMessageChannel, setOpenMessageChannel] = useState<number | null>(
        null,
    )

    const { workspaceUser } = useUser()

    const Ref = useRef<any>(null)

    // The state for our timer
    const [timer, setTimer] = useState({
        total: 0,
        seconds: 0,
        minutes: 0,
        hours: 0,
        days: 0,
    })

    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        open: boolean
        deadlineId: number | null
    }>({
        open: false,
        deadlineId: null,
    })

    const [addApartmentsOpen, setAddApartmentsOpen] = useState(false)

    const getTimeRemaining = (deadlineDate: Date) => {
        const total = deadlineDate.getTime() - new Date().getTime()
        const seconds = Math.floor((total / 1000) % 60)
        const minutes = Math.floor((total / 1000 / 60) % 60)
        const hours = Math.floor((total / 1000 / 60 / 60) % 24)
        const days = Math.floor(total / 1000 / 60 / 60 / 24)
        return {
            total,
            hours,
            minutes,
            seconds,
            days,
        }
    }

    const tickTimer = (deadlineDate: Date) => {
        const remaining = getTimeRemaining(deadlineDate)
        if (remaining.total >= 0) {
            setTimer(remaining)
        }
    }

    const startTimer = (date: Date) => {
        tickTimer(date)
        // If you try to remove this line the
        // updating of timer Variable will be
        // after 1000ms or 1sec
        if (Ref.current) clearInterval(Ref.current)
        const id = setInterval(() => {
            tickTimer(date)
        }, 1000)
        Ref.current = id
    }

    // const open = formState.popovers.deadlinePopover.open
    const open = formState.modals.deadlineModal.open
    const apartmentDeadline = deadlineList.find(
        (dl) => dl.id === formState.modals.deadlineModal.deadlineId,
    )

    useEffect(() => {
        if (open && apartmentDeadline) {
            getDeadlineDetail(apartmentDeadline.deadline.id).then(
                (deadlineDetail) => {
                    setDeadlineDetail(deadlineDetail)
                },
            )
            startTimer(deadlineDate)
        }

        if (!open) {
            setDeadlineDetail(null)
            setOpenMessageChannel(null)
        }
    }, [formState])

    const deadlineDate = new Date(apartmentDeadline?.deadline.date ?? '')

    let descriptionLabel = apartmentDeadline?.deadline.description

    if (descriptionLabel === '' || descriptionLabel === undefined) {
        descriptionLabel = 'N/A'
    }

    let activeAptDetail: ApartmentDeadline | null = null
    const otherAptDetails: ApartmentDeadline[] = []

    deadlineDetail?.apartment_deadlines.forEach((aptDeadline) => {
        if (workspaceUser?.active_workspace.id === aptDeadline.apartment.id) {
            activeAptDetail = apartmentDeadline ?? null
        } else {
            otherAptDetails.push(aptDeadline)
        }
    })

    const setMessageDrawerOpen = (channelId: number) => {
        if (openMessageChannel === channelId) {
            setOpenMessageChannel(null)
        } else {
            setOpenMessageChannel(channelId)
        }
    }

    const handleDelete = (id: number) => {
        setDeleteConfirmation({ open: true, deadlineId: id })
    }

    const confirmDelete = () => {
        if (deleteConfirmation.deadlineId) {
            deleteApartmentDeadline(deleteConfirmation.deadlineId).then(() => {
                // If we're deleting the active apartment deadline, close the modal
                if (
                    activeAptDetail &&
                    activeAptDetail.id === deleteConfirmation.deadlineId
                ) {
                    setDeadlineDetail(null)
                    onClose()
                } else if (deadlineDetail) {
                    // Otherwise just update the list of deadlines
                    setDeadlineDetail({
                        ...deadlineDetail,
                        apartment_deadlines: deadlineDetail.apartment_deadlines.filter(
                            (aptDl) =>
                                aptDl.id !== deleteConfirmation.deadlineId,
                        ),
                    })
                }
                setDeleteConfirmation({ open: false, deadlineId: null })
                toast.success('Deadline deleted')
            })
        }
    }

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper
                        style={{
                            display: 'flex',
                            minHeight: h,
                            maxHeight: h,
                            minWidth: w,
                            maxWidth: w,
                        }}
                    >
                        {/* Content */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                flex: 1,
                                overflowY: 'scroll',
                            }}
                        >
                            {/* Title */}
                            <Container
                                style={{
                                    minHeight: 100,
                                    maxHeight: 100,
                                    borderBottom: `1px solid ${theme.palette.grey[700]}`,
                                    flex: 1,
                                    padding: theme.spacing(1),
                                    alignItems: 'center',
                                }}
                            >
                                <Container style={{ flexDirection: 'column' }}>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.h6,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            color: theme.palette.grey[800],
                                        }}
                                    >
                                        {apartmentDeadline?.deadline.name}
                                    </span>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle2,
                                            ),
                                            fontWeight:
                                                theme.typography
                                                    .fontWeightLight,
                                            color: theme.palette.grey[700],
                                        }}
                                    >
                                        Due: {toMMDDYYYY(deadlineDate)}
                                    </span>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle2,
                                            ),
                                            fontWeight:
                                                theme.typography
                                                    .fontWeightLight,
                                            color: theme.palette.grey[600],
                                        }}
                                    >
                                        {apartmentDeadline?.deadline
                                            .reminder_date
                                            ? `Reminder: ${toMMDDYYYY(
                                                  new Date(
                                                      apartmentDeadline.deadline.reminder_date,
                                                  ),
                                              )}`
                                            : 'No reminder set'}
                                    </span>
                                </Container>
                                <div style={{ flex: 1 }} />
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle2,
                                            ),
                                            fontWeight:
                                                theme.typography
                                                    .fontWeightLight,
                                            color: theme.palette.grey[700],
                                        }}
                                    >
                                        Time remaining
                                    </span>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle1,
                                            ),
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                            color: theme.palette.grey[800],
                                        }}
                                    >
                                        {timer.days} Days {timer.hours} Hours{' '}
                                        {timer.minutes} Minutes {timer.seconds}{' '}
                                        Seconds
                                    </span>
                                </Container>
                                <div style={{ flex: 1 }} />
                            </Container>
                            {/* Body */}

                            <Container style={{ flex: 1 }}>
                                <Container
                                    style={{
                                        flex: 1,
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Row 1, Description */}
                                    <Container
                                        style={{
                                            padding: theme.spacing(1),
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.subtitle2,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                                color: theme.palette.grey[800],
                                            }}
                                        >
                                            Description
                                        </span>
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.body1,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightLight,
                                                color: theme.palette.grey[700],
                                            }}
                                        >
                                            {descriptionLabel}
                                        </span>
                                    </Container>

                                    {/* Row 2 Active Workspace */}
                                    <Container
                                        style={{
                                            padding: theme.spacing(1),
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <span
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.subtitle2,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                                color: theme.palette.grey[800],
                                            }}
                                        >
                                            Your Workspace
                                        </span>
                                        {activeAptDetail && (
                                            <DeadlineRow
                                                theme={theme}
                                                workspaceName={
                                                    workspaceUser
                                                        ?.active_workspace
                                                        .name ?? ''
                                                }
                                                apartmentDeadline={
                                                    activeAptDetail
                                                }
                                                selectedChannel={
                                                    openMessageChannel
                                                }
                                                handleClickMessage={
                                                    setMessageDrawerOpen
                                                }
                                                transitionDeadline={(
                                                    id,
                                                    status,
                                                ) => {
                                                    transitionDeadline(
                                                        id,
                                                        status,
                                                    ).then((deadline) => {
                                                        setDeadlineDetail(
                                                            deadline,
                                                        )
                                                    })
                                                }}
                                                onDelete={handleDelete}
                                            />
                                        )}
                                    </Container>

                                    {/* Row 3 Other workspace deadlines */}
                                    <Container
                                        style={{
                                            padding: theme.spacing(1),
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {otherAptDetails.length > 0 && (
                                            <span
                                                style={{
                                                    ...typographyStyle(
                                                        theme.typography
                                                            .subtitle2,
                                                    ),
                                                    fontWeight:
                                                        theme.typography
                                                            .fontWeightBold,
                                                    color:
                                                        theme.palette.grey[800],
                                                }}
                                            >
                                                Additional Workspaces
                                            </span>
                                        )}
                                        {otherAptDetails.map((deadline) => {
                                            return (
                                                <DeadlineRow
                                                    key={`DEADLINE-${deadline.id}`}
                                                    workspaceName={
                                                        deadline.apartment.name
                                                    }
                                                    handleClickMessage={
                                                        setMessageDrawerOpen
                                                    }
                                                    theme={theme}
                                                    apartmentDeadline={deadline}
                                                    selectedChannel={
                                                        openMessageChannel
                                                    }
                                                    onDelete={handleDelete}
                                                />
                                            )
                                        })}
                                    </Container>
                                </Container>

                                {openMessageChannel !== null && (
                                    <Container
                                        style={{
                                            width: 400,
                                            borderLeft: `1px solid ${theme.palette.grey[700]}`,
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Container
                                            style={{
                                                padding: theme.spacing(1),
                                                alignItems: 'center',
                                            }}
                                        >
                                            <IconButton
                                                onClick={() => {
                                                    setOpenMessageChannel(null)
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>

                                            <span
                                                style={{
                                                    ...typographyStyle(
                                                        theme.typography.h5,
                                                    ),
                                                    fontWeight:
                                                        theme.typography
                                                            .fontWeightBold,
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            >
                                                Messages
                                            </span>
                                        </Container>
                                        <MessageForm
                                            theme={theme}
                                            channelId={openMessageChannel}
                                            afterCreateMessage={() => {
                                                if (apartmentDeadline) {
                                                    incrementMessageCount(
                                                        apartmentDeadline,
                                                    )
                                                }
                                            }}
                                        />
                                    </Container>
                                )}
                            </Container>

                            {/* Footer */}
                            <Container
                                style={{
                                    minHeight: 100,
                                    maxHeight: 100,
                                    borderTop: `1px solid ${theme.palette.grey[700]}`,
                                    flex: 1,
                                    padding: theme.spacing(1),
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    style={{
                                        width: 150,
                                        textTransform: 'none',
                                        height: 50,
                                        marginRight: theme.spacing(1),
                                    }}
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <div style={{ flex: 1 }} />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{
                                        width: 150,
                                        textTransform: 'none',
                                        height: 50,
                                        marginRight: theme.spacing(1),
                                    }}
                                    onClick={() => setAddApartmentsOpen(true)}
                                >
                                    Add Apartments
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    style={{
                                        width: 150,
                                        textTransform: 'none',
                                        height: 50,
                                        marginRight: theme.spacing(1),
                                    }}
                                    onClick={() => {
                                        if (open && apartmentDeadline) {
                                            getDeadlineDetail(
                                                apartmentDeadline.deadline.id,
                                            ).then((deadlineDetail) => {
                                                console.log(deadlineDetail)
                                            })
                                        }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Container>
                        </Container>
                    </Paper>
                </Slide>
            </Modal>
            <Dialog
                open={deleteConfirmation.open}
                onClose={() =>
                    setDeleteConfirmation({ open: false, deadlineId: null })
                }
            >
                <DialogTitle>Delete Deadline</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this deadline? This action
                    cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setDeleteConfirmation({
                                open: false,
                                deadlineId: null,
                            })
                        }
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="secondary"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Modal
                open={addApartmentsOpen}
                onClose={() => setAddApartmentsOpen(false)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={addApartmentsOpen}>
                    <Paper
                        style={{
                            display: 'flex',
                            minHeight: h,
                            maxHeight: h,
                            minWidth: w,
                            maxWidth: w,
                        }}
                    >
                        <AddApartmentsContent
                            theme={theme}
                            onClose={() => setAddApartmentsOpen(false)}
                            deadlineId={apartmentDeadline?.deadline.id ?? 0}
                            addApartmentDeadlines={(apartmentIds) => {
                                if (apartmentDeadline) {
                                    addApartmentDeadlines({
                                        deadline_id:
                                            apartmentDeadline.deadline.id,
                                        apartment_ids: apartmentIds,
                                    })
                                        .then((updatedDetail) => {
                                            setDeadlineDetail(updatedDetail)
                                            setAddApartmentsOpen(false)
                                            toast.success(
                                                'Apartments added successfully',
                                            )
                                        })
                                        .catch((e) => {
                                            toast.error(
                                                'Failed to add apartments',
                                            )
                                        })
                                }
                            }}
                            existingApartmentIds={
                                deadlineDetail?.apartment_deadlines.map(
                                    (ad) => ad.apartment.id,
                                ) ?? []
                            }
                        />
                    </Paper>
                </Slide>
            </Modal>
        </>
    )
}

interface AddApartmentsContentProps {
    theme: Theme
    onClose: () => void
    deadlineId: number
    addApartmentDeadlines: (apartmentIds: number[]) => void
    existingApartmentIds: number[]
}

const AddApartmentsContent = (props: AddApartmentsContentProps) => {
    const {
        theme,
        onClose,
        addApartmentDeadlines,
        existingApartmentIds,
    } = props
    const { rootUser, workspaceUser } = useUser()
    const [
        selectedPropertyState,
        setSelectedPropertyState,
    ] = useState<IdBoolMap>({})
    const [searchQuery, setSearchQuery] = useState('')

    const filteredWorkspaces =
        rootUser?.workspaces.filter((wsOption) => {
            return (
                wsOption.active_workspace.organization ===
                    workspaceUser?.active_workspace.organization &&
                !existingApartmentIds.includes(wsOption.active_workspace.id) &&
                wsOption.active_workspace.company_type === APARTMENT
            )
        }) ?? []

    const searchFilteredWorkspaces = filteredWorkspaces.filter((ws) =>
        ws.active_workspace.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
    )

    const selectedIds = Object.keys(selectedPropertyState).reduce<number[]>(
        (prev, strKey) => {
            const key = Number(strKey)
            if (selectedPropertyState[key]) {
                return prev.concat(key)
            }
            return prev
        },
        [],
    )

    const selectedCount = selectedIds.length

    return (
        <Container style={{ flexDirection: 'column', flex: 1 }}>
            {/* Title */}
            <Container
                style={{
                    minHeight: 100,
                    maxHeight: 100,
                    borderBottom: `1px solid ${theme.palette.grey[700]}`,
                    padding: theme.spacing(1),
                    alignItems: 'center',
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 700,
                    }}
                >
                    Add apartments to deadline
                </span>
            </Container>

            {/* Body */}
            <Container
                style={{ flexDirection: 'column', flex: 1, overflow: 'hidden' }}
            >
                {/* Search bar */}
                <Container
                    style={{
                        padding: theme.spacing(1),
                        borderBottom: `1px solid ${theme.palette.grey[500]}`,
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder="Search apartments..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Container>

                {/* Select all row */}
                <Container
                    style={{
                        padding: theme.spacing(1),
                        alignItems: 'center',
                        borderBottom: `1px solid ${theme.palette.grey[500]}`,
                    }}
                >
                    <Checkbox
                        style={{
                            marginRight: theme.spacing(1),
                            marginLeft: theme.spacing(1),
                        }}
                        checked={
                            selectedCount === searchFilteredWorkspaces.length &&
                            searchFilteredWorkspaces.length > 0
                        }
                        indeterminate={
                            selectedCount > 0 &&
                            selectedCount < searchFilteredWorkspaces.length
                        }
                        onChange={() => {
                            if (selectedCount > 0) {
                                setSelectedPropertyState({})
                            } else {
                                const newState: IdBoolMap = {}
                                searchFilteredWorkspaces.forEach((ws) => {
                                    newState[ws.active_workspace.id] = true
                                })
                                setSelectedPropertyState(newState)
                            }
                        }}
                    />
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 500,
                            color: theme.palette.grey[600],
                        }}
                    >
                        Select all available apartments
                    </span>
                </Container>

                {/* Workspace list - Scrollable */}
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflowY: 'auto',
                    }}
                >
                    {searchFilteredWorkspaces.map((ws) => {
                        const propertyId = ws.active_workspace.id
                        const isChecked =
                            selectedPropertyState[propertyId] === true

                        const onClick = () => {
                            setSelectedPropertyState({
                                ...selectedPropertyState,
                                [propertyId]: !isChecked,
                            })
                        }

                        return (
                            <Container
                                key={`WS-${propertyId}`}
                                style={{
                                    padding: theme.spacing(1),
                                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                    margin: theme.spacing(1),
                                    alignItems: 'center',
                                    height: 75,
                                    cursor: 'pointer',
                                }}
                                onClick={onClick}
                            >
                                <Checkbox
                                    style={{ marginRight: theme.spacing(1) }}
                                    checked={isChecked}
                                />
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightMedium,
                                        color: theme.palette.grey[800],
                                    }}
                                >
                                    {ws.active_workspace.name}
                                </span>
                            </Container>
                        )
                    })}
                </Container>
            </Container>

            {/* Footer */}
            <Container
                style={{
                    minHeight: 100,
                    maxHeight: 100,
                    borderTop: `1px solid ${theme.palette.grey[700]}`,
                    padding: theme.spacing(1),
                    alignItems: 'center',
                }}
            >
                <Button
                    variant="contained"
                    color="secondary"
                    style={{
                        width: 150,
                        textTransform: 'none',
                        height: 50,
                        marginRight: theme.spacing(1),
                    }}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <div style={{ flex: 1 }} />
                <Button
                    variant="contained"
                    color="primary"
                    style={{
                        width: 150,
                        textTransform: 'none',
                        height: 50,
                    }}
                    disabled={selectedCount === 0}
                    onClick={() => addApartmentDeadlines(selectedIds)}
                >
                    Add ({selectedCount})
                </Button>
            </Container>
        </Container>
    )
}
